import { LiveData, useLiveData, useService } from '@toeverything/infra';
import { useEffect, useMemo } from 'react';
import { map } from 'rxjs';

import { AccessibleDocCacheStore } from '../../doc/stores/accessible-doc-cache';
import { WorkspaceProjectionReconciler } from '../../workspace-projection-reconciliation';
import type { Workspace } from '../entities/workspace';
import { resolveRoleAwareRootDocReady } from './role-aware-root-doc-ready';

/**
 * sl-284b.3: Workspace ready gate.
 *
 * Cloud workspaces (all roles): Waits for AccessibleDocCacheStore.isPopulated$
 * (IDB cache loads in ~0ms, server fetch runs async in background).
 * DocsStore already uses AccessibleDocCache for the doc list — the gate must
 * match the data source to avoid blocking on an unused Y.js sync.
 *
 * Local workspaces: Waits for Y.js root doc sync (contains meta.pages).
 *
 * Y.js root doc sync still runs in background for cloud workspaces
 * (needed for content editing and tags), but does not block the UI.
 *
 * Projection invalidations are delegated to the workspace-scoped reconciler,
 * which owns the engine subscriptions exactly once.
 *
 * DSGVO: On workspace_member_revoked, deletes all IDB databases for that
 * workspace and redirects to the workspace list for clean re-entry.
 */
export function useRoleAwareRootDocReady(
  workspace: Workspace | null,
  options: { requirePermissionCache?: boolean } = {}
): boolean {
  const accessibleDocCache = useService(AccessibleDocCacheStore);
  const projectionReconciler = useService(WorkspaceProjectionReconciler);
  const isPopulated = useLiveData(accessibleDocCache.isPopulated$);

  const isRootDocSynced =
    useLiveData(
      useMemo(
        () =>
          workspace
            ? LiveData.from(
                workspace.engine.doc
                  .docState$(workspace.id)
                  .pipe(map(v => (v as { ready: boolean }).ready)),
                false
              )
            : null,
        [workspace]
      )
    ) ?? false;

  const isLocal = !workspace || workspace.flavour === 'local';
  const requirePermissionCache = options.requirePermissionCache ?? true;

  useEffect(() => {
    if (isLocal) {
      return;
    }
    accessibleDocCache.prime();
    projectionReconciler.prime();
  }, [accessibleDocCache, isLocal, projectionReconciler, workspace?.id]);

  return resolveRoleAwareRootDocReady({
    isLocal,
    isRootDocSynced,
    isPermissionCachePopulated: isPopulated,
    requirePermissionCache,
  });
}
